import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBoundStore } from '../store';
import { useI18n } from '../i18n';
import { buildTours, type TourId } from './tours';
import { runTour } from './useDriverTour';

// Canonical route a tour needs to run on. `review` runs in-place (it needs an
// open chat), so it has no canonical route.
const ROUTE_FOR: Record<TourId, string | null> = {
  welcome: '/',
  playbooks: '/playbooks',
  review: null,
};

/**
 * Imperatively launch a tour (used by the "Help & tips" menu). Navigates to the
 * tour's route first when needed, then runs it after a short delay so anchors
 * have mounted.
 */
export function useOnboarding() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const startTour = useBoundStore((s) => s.startTour);
  const markTourSeen = useBoundStore((s) => s.markTourSeen);

  const launch = useCallback(
    (id: TourId) => {
      const tour = buildTours(t)[id];
      if (!tour) return;
      const run = () => {
        startTour(id);
        runTour(tour, t, () => markTourSeen(id, id === 'welcome'));
      };
      const target = ROUTE_FOR[id];
      if (target && window.location.pathname !== target) {
        navigate(target);
        setTimeout(run, 900);
      } else {
        setTimeout(run, 200);
      }
    },
    [t, navigate, startTour, markTourSeen],
  );

  return { launchTour: launch };
}
